import { Switch } from "@sagot/ui";
import { useId, type ReactNode } from "react";
import { Controller, useFormContext, type FieldPathByValue } from "react-hook-form";
import type { SettingsValues } from "./settings-model";

/** Settings card (mockup `.card` + `.card-header` title/subtitle + body). */
export function SettingsCard({ title, hint, aside, children, flush = false }: { title: ReactNode; hint?: ReactNode; aside?: ReactNode; children: ReactNode; flush?: boolean }) {
  const id = useId();
  return (
    <section className="card overflow-hidden" aria-labelledby={id}>
      <div className="card-header">
        <div>
          <h2 id={id} className="card-title">{title}</h2>
          {hint ? <p className="mt-0.5 text-xs text-fg-subtle">{hint}</p> : null}
        </div>
        {aside}
      </div>
      {flush ? children : <div className="card-body space-y-5">{children}</div>}
    </section>
  );
}

type BooleanPath = FieldPathByValue<SettingsValues, boolean>;

interface SwitchRowProps {
  name: BooleanPath;
  label: ReactNode;
  hint?: ReactNode;
  disabled: boolean;
  className?: string;
}

/** Label + hint left, switch right (label and description wired for screen readers). */
export function SwitchRow({ name, label, hint, disabled, className = "px-5 py-4" }: SwitchRowProps) {
  const { control } = useFormContext<SettingsValues>();
  const id = useId();
  return (
    <div className={`flex items-start justify-between gap-4 ${className}`}>
      <div>
        <label id={`${id}-name`} htmlFor={id} className="text-sm font-semibold">{label}</label>
        {hint ? <p id={`${id}-hint`} className="mt-0.5 text-xs text-fg-subtle">{hint}</p> : null}
      </div>
      <Controller
        control={control}
        name={name}
        render={({ field }) => (
          <Switch id={id} aria-labelledby={`${id}-name`} aria-describedby={hint ? `${id}-hint` : undefined} className="mt-0.5 shrink-0" checked={field.value} onCheckedChange={field.onChange} disabled={disabled} />
        )}
      />
    </div>
  );
}
